/**
 * Sanh tro choi.
 *
 * Bo cuc luoi day: nguoi choi quet mat qua nhieu tua cung luc, nen the phai nho,
 * anh chiem phan lon dien tich, chu chi la nhan phu. Day la chuan muc cua dong
 * san pham va cung dung ve chuc nang.
 *
 * CAS-R61: nha cung cap gian doan thi tro choi hien trang thai BAO TRI, khong
 * bien mat khoi sanh — bien mat dot ngot lam nguoi choi tuong mat tai khoan.
 */

import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type GameSummary } from '../lib/api';
import { formatMoney } from '../lib/money';
import { useSession } from '../lib/session';
import { Badge, EmptyState, Skeleton } from '../components/ui';
import styles from './Lobby.module.css';

const CATEGORIES = [
  { code: undefined, label: 'Tất cả' },
  { code: 'SLOTS', label: 'Quay hũ' },
  { code: 'LIVE_CASINO', label: 'Sòng bài trực tiếp' },
  { code: 'TABLE_GAMES', label: 'Trò chơi bàn' },
  { code: 'FISHING', label: 'Bắn cá' },
  { code: 'INSTANT', label: 'Tức thời' },
] as const;

const SORTS = [
  { code: 'POPULAR', label: 'Phổ biến' },
  { code: 'NEW', label: 'Mới' },
  { code: 'NAME', label: 'Tên' },
  { code: 'RTP', label: 'Tỷ lệ trả thưởng' },
] as const;

export function LobbyPage({ fixedCategory }: { fixedCategory?: string }): JSX.Element {
  const { isAuthenticated } = useSession();
  const [category, setCategory] = useState<string | undefined>(fixedCategory);
  const [sort, setSort] = useState<string>('POPULAR');
  const [search, setSearch] = useState('');

  const lobby = useQuery({
    queryKey: ['lobby', category, sort, search],
    queryFn: () =>
      api.casino.lobby({
        ...(category ? { category } : {}),
        ...(search ? { search } : {}),
        sort,
      }),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  const jackpots = useQuery({
    queryKey: ['jackpots'],
    queryFn: api.casino.jackpots,
    enabled: isAuthenticated,
    // Gia tri hu doi lien tuc; CAS-R43 doi phai kem moc cap nhat.
    refetchInterval: 30_000,
  });

  if (!isAuthenticated) {
    return (
      <EmptyState
        title="Đăng nhập để xem sảnh trò chơi"
        description="Danh mục trò chơi được lọc theo quốc gia và tiền tệ của từng tài khoản."
      />
    );
  }

  return (
    <div className={styles.page}>
      {jackpots.data && jackpots.data.jackpots.length > 0 && (
        <div className={styles.jackpotBar}>
          {jackpots.data.jackpots.map((j) => (
            <div key={j.code} className={styles.jackpot}>
              <span className={styles.jackpotName}>{j.display_name}</span>
              <span className={`numeric ${styles.jackpotAmount}`}>
                {formatMoney(j.amount)} <span>{j.amount.currency}</span>
              </span>
              {/* CAS-R43: gia tri hu luon kem moc thoi gian cap nhat */}
              <span className={styles.jackpotTime}>
                cập nhật {new Date(j.updated_at).toLocaleTimeString('vi-VN')}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className={styles.toolbar}>
        {!fixedCategory && (
          <div className={styles.categories} role="tablist" aria-label="Nhóm trò chơi">
            {CATEGORIES.map((c) => (
              <button
                key={c.label}
                role="tab"
                aria-selected={category === c.code}
                className={[styles.chip, category === c.code ? styles.chipActive : '']
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setCategory(c.code)}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}

        <div className={styles.controls}>
          <input
            className={styles.search}
            type="search"
            placeholder="Tìm trò chơi"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Tìm trò chơi"
          />
          <select
            className={styles.sort}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label="Sắp xếp"
          >
            {SORTS.map((s) => (
              <option key={s.code} value={s.code}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {lobby.isLoading ? (
        <div className={styles.grid}>
          {Array.from({ length: 18 }, (_, i) => (
            <div key={i} className={styles.card}>
              <Skeleton height={132} />
            </div>
          ))}
        </div>
      ) : lobby.data && lobby.data.games.length > 0 ? (
        <>
          <p className={styles.count}>{lobby.data.total} trò chơi</p>
          <div className={styles.grid}>
            {lobby.data.games.map((game) => (
              <GameCard key={game.code} game={game} />
            ))}
          </div>
        </>
      ) : (
        <EmptyState
          title="Không tìm thấy trò chơi nào"
          description={
            search
              ? 'Thử từ khoá khác, hoặc bỏ bớt bộ lọc.'
              : 'Danh mục trò chơi chưa được đồng bộ từ nhà cung cấp.'
          }
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function GameCard({ game }: { game: GameSummary }): JSX.Element {
  const unavailable = game.status !== 'ACTIVE';

  const open = async (mode: 'REAL' | 'DEMO'): Promise<void> => {
    const session = await api.casino.openSession(game.code, mode);
    // CAS-R20: duong dan khoi chay dung MOT lan va co thoi han ngan.
    window.open(session.launch_url, '_blank', 'noopener');
  };

  return (
    <article className={[styles.card, unavailable ? styles.cardOff : ''].filter(Boolean).join(' ')}>
      <div className={styles.thumb}>
        {game.thumbnail_url ? (
          <img src={game.thumbnail_url} alt="" loading="lazy" />
        ) : (
          <span className={styles.thumbFallback} aria-hidden="true">
            {game.name.slice(0, 2).toUpperCase()}
          </span>
        )}

        {game.has_jackpot && (
          <span className={styles.jackpotTag}>
            <Badge tone="brand">Hũ</Badge>
          </span>
        )}

        {unavailable ? (
          <div className={styles.overlay}>
            <Badge tone="warning">Bảo trì</Badge>
          </div>
        ) : (
          <div className={styles.actions}>
            <button className={styles.play} onClick={() => void open('REAL')}>
              Chơi
            </button>
            {game.has_demo && (
              <button className={styles.demo} onClick={() => void open('DEMO')}>
                Thử
              </button>
            )}
          </div>
        )}
      </div>

      <div className={styles.meta}>
        <span className={styles.name} title={game.name}>{game.name}</span>
        {game.rtp_bp !== null && (
          <span className={`numeric ${styles.rtp}`}>{(game.rtp_bp / 100).toFixed(2)}%</span>
        )}
      </div>
    </article>
  );
}
